import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { PrivatesAppointmentsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { invalidateAndRefetchAppointmentBookings } from "@/features/appointments/utils/appointment-bookings-query";
import { invalidatePostedTuitionLedgerQueries } from "@/features/tuition/hooks/invalidate-posted-tuition-ledger-queries";
import { useToast } from "@/components/feedback";

export const useBookingCancel = () => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: async (bookingId: number) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(
        PrivatesAppointmentsApi,
        accessToken,
      );
      return service.apiAppointmentsBookingIdCancelPut({
        id: bookingId,
      });
    },
    onSuccess: async () => {
      await invalidateAndRefetchAppointmentBookings(queryClient);
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Appointments],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.AppointmentAvailability],
      });
      await invalidatePostedTuitionLedgerQueries(queryClient);
      toast({
        title: "Booking canceled",
        description: "Your private lesson booking has been canceled successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to cancel booking";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending };
};

